<template>
  <ResponsiveLayout :progress="progress">
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__doctor_outreach_add_prescriber__title') }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ t('eu__doctor_outreach_add_prescriber__description') }}
        </p>
      </div>

      <form
        id="prescriber-information-form"
        data-qa="prescriber-information"
        class="kds-content-block"
        autocomplete="off"
      >
        <TextField
          :label="$t('eu__prescriber_information__prescriber_name')"
          data-qa="prescriber-name"
          v-bind="fieldAttrs.prescriberName"
        />
        <TextField
          :label="$t('eu__prescriber_information__city_or_zip')"
          data-qa="city-or-zip"
          v-bind="fieldAttrs.cityOrZip"
        />
      </form>
    </div>

    <template #actions>
      <BaseButton
        :text="$t('generic_action_continue')"
        data-qa="submit"
        v-bind="submitAttrs"
        type="submit"
        form="prescriber-information-form"
      />
      <BaseButton
        :text="$t('eu__doctor_outreach_add_prescriber__action_doctor_search')"
        variant="outlined"
        data-qa="doctor-search"
        @click="navigateToDoctorSearch"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import BaseButton from '@shared/components/BaseButton.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import TextField from '@shared/components/form/TextField.vue'
import useForm from '@shared/composables/useForm.js'
import useDoctorOutreachStore from '@eu/app/stores/useDoctorOutreachStore.js'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import { useI18n } from 'petite-vue-i18n'
import Tracker from '@shared/Tracker.js'

const { progress } = useOnboardingProgress()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.DOCTOR_OUTREACH_ADD_PRESCRIBER,
)
const doctorOutreachStore = useDoctorOutreachStore()
const { t } = useI18n()

const form = ref({
  prescriberName: doctorOutreachStore.prescriberRequest?.prescriberName ?? '',
  cityOrZip:
    doctorOutreachStore.prescriberRequest?.zipCode ||
    doctorOutreachStore.prescriberRequest?.place ||
    '',
})

const { fieldAttrs, submitAttrs } = useForm(
  form,
  ref({
    prescriberName: {
      validations: ['required'],
    },
    cityOrZip: { validations: ['required'] },
  }),
  {
    onSubmit: () => {},
    onError: () => {},
    onSuccess: () => {
      Tracker.trackButtonSelect('submit', 'doctor_outreach_add_prescriber')
      doctorOutreachStore.selectedPracticeId = null
      navigateNext('prescriberEntered')
    },
  },
)

watch(
  form,
  (value) => {
    const cityOrZip = value.cityOrZip.trim()
    const isGermanZip = /^\d{5}$/.test(cityOrZip)
    doctorOutreachStore.prescriberRequest = {
      prescriberName: value.prescriberName,
      place: isGermanZip ? '' : cityOrZip,
      zipCode: isGermanZip ? cityOrZip : '',
    }
  },
  { deep: true },
)

const navigateToDoctorSearch = () => {
  Tracker.trackButtonSelect('doctorSearch', 'doctor_outreach_add_prescriber')
  navigateNext('searchPrescriber')
}
</script>
